import React, { lazy } from 'react';
import { logger } from '@/services/logger';

// Import critical components directly to avoid lazy loading issues
import Dashboard from '@/pages/Dashboard';
import Login from '@/pages/Login';
import Supervision from '@/pages/Supervision';
import Support from '@/pages/Support';
import Community from '@/pages/Community';
import ResetPassword from '@/pages/ResetPassword';
import About from '@/pages/About';
import DebugReset from '@/pages/DebugReset';
import Verify from '@/pages/Verify';
import InvitationsAdmin from '@/pages/InvitationsAdmin';
import UserManagement from '@/pages/admin/UserManagement';

// Lazy load non-critical components
const Clients = lazy(() => {
  logger.debug('Loading Clients component');
  return import('@/pages/Clients');
});

const Sessions = lazy(() => {
  logger.debug('Loading Sessions component');
  return import('@/pages/Sessions');
});

const SessionDetail = lazy(() => {
  logger.debug('Loading SessionDetail component');
  return import('@/pages/SessionDetail');
});

const Reports = lazy(() => {
  logger.debug('Loading Reports component');
  return import('@/pages/Reports');
});

const ExportImport = lazy(() => {
  logger.debug('Loading ExportImport component');
  return import('@/pages/ExportImport');
});

const NotFound = lazy(() => {
  logger.debug('Loading NotFound component');
  return import('@/pages/NotFound');
});

const Index = lazy(() => {
  logger.debug('Loading Index component');
  return import('@/pages/Index');
});

const Signup = lazy(() => {
  logger.debug('Loading Signup component');
  return import('@/pages/Signup');
});

const Profile = lazy(() => {
  logger.debug('Loading Profile component');
  return import('@/pages/Profile');
});

const Admin = lazy(() => {
  logger.debug('Loading Admin component');
  return import('@/pages/Admin');
});

const CoachTools = lazy(() => {
  logger.debug('Loading CoachTools component');
  return import('@/pages/CoachTools');
});

const Account = lazy(() => {
  logger.debug('Loading Account component');
  return import('@/pages/Account');
});

const CPD = lazy(() => {
  logger.debug('Loading CPD component');
  return import('@/pages/CPD');
});

const UserDetail = lazy(() => {
  logger.debug('Loading UserDetail component');
  return import('@/pages/admin/UserDetail');
});

export interface RouteConfig {
  path: string;
  element: React.ReactElement;
  isPublic?: boolean;
  requireAdmin?: boolean;
  requireSubscription?: boolean;
  name?: string;
  description?: string;
}

// Route configuration - ensuring supervision routes are correctly defined
export const routes: RouteConfig[] = [
  {
    path: '/login',
    element: <Login />,
    isPublic: true,
    name: 'Login',
    description: 'User authentication'
  },
  {
    path: '/signup',
    element: <Signup />,
    isPublic: true,
    name: 'Signup',
    description: 'User registration'
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
    isPublic: true,
    name: 'Reset Password',
    description: 'Password reset flow'
  },
  {
    path: '/verify',
    element: <Verify />,
    isPublic: true,
    name: 'Verify',
    description: 'Email verification'
  },
  {
    path: '/debug-reset',
    element: <DebugReset />,
    isPublic: true,
    name: 'Debug Reset',
    description: 'Debug password reset'
  },
  {
    path: '/',
    element: <Index />,
    isPublic: true,
    name: 'Home',
    description: 'Landing page'
  },
  {
    path: '/dashboard',
    element: <Dashboard />,
    requireSubscription: true,
    name: 'Dashboard',
    description: 'Main dashboard'
  },
  {
    path: '/clients',
    element: <Clients />,
    requireSubscription: true,
    name: 'Clients',
    description: 'Client management'
  },
  {
    path: '/sessions',
    element: <Sessions />,
    requireSubscription: true,
    name: 'Sessions',
    description: 'Session management'
  },
  {
    path: '/sessions/:id',
    element: <SessionDetail />,
    requireSubscription: true,
    name: 'Session Detail',
    description: 'Individual session details'
  },
  {
    path: '/reports',
    element: <Reports />,
    requireSubscription: true,
    name: 'Reports',
    description: 'Analytics and reports'
  },
  {
    path: '/export-import',
    element: <ExportImport />,
    requireSubscription: true,
    name: 'Export/Import',
    description: 'Data export and import'
  },
  {
    path: '/account',
    element: <Account />,
    requireSubscription: true,
    name: 'Account',
    description: 'Account settings'
  },
  {
    path: '/profile',
    element: <Profile />,
    requireSubscription: true,
    name: 'Profile',
    description: 'User profile'
  },
  {
    path: '/admin',
    element: <Admin />,
    requireAdmin: true,
    name: 'Admin',
    description: 'Admin dashboard'
  },
  {
    path: '/admin/users',
    element: <UserManagement />,
    requireAdmin: true,
    name: 'User Management',
    description: 'Manage users'
  },
  {
    path: '/admin/users/:userId',
    element: <UserDetail />,
    requireAdmin: true,
    name: 'User Detail',
    description: 'Individual user details'
  },
  {
    path: '/admin/invitations',
    element: <InvitationsAdmin />,
    requireAdmin: true,
    name: 'Invitations Admin',
    description: 'Manage user invitations'
  },
  {
    path: '/coach-tools',
    element: <CoachTools />,
    requireSubscription: true,
    name: 'Coach Tools',
    description: 'Coaching tools and resources'
  },
  {
    path: '/supervision',
    element: <Supervision />,
    requireSubscription: true,
    name: 'Supervision',
    description: 'Supervision management'
  },
  {
    path: '/supervision/new',
    element: <Supervision />,
    requireSubscription: true,
    name: 'New Supervision',
    description: 'Create new supervision session'
  },
  {
    path: '/supervision/edit/:id',
    element: <Supervision />,
    requireSubscription: true,
    name: 'Edit Supervision',
    description: 'Edit supervision session'
  },
  {
    path: '/cpd',
    element: <CPD />,
    requireSubscription: true,
    name: 'CPD',
    description: 'Continuous Professional Development'
  },
  {
    path: '/support',
    element: <Support />,
    requireSubscription: true,
    name: 'Support',
    description: 'Customer support'
  },
  {
    path: '/community',
    element: <Community />,
    requireSubscription: true,
    name: 'Community',
    description: 'User community'
  },
  {
    path: '/admin/invitations',
    element: <InvitationsAdmin />,
    requireAdmin: true,
    name: 'Invitations Admin',
    description: 'Manage user invitations'
  },
  {
    path: '/about',
    element: <About />,
    name: 'About',
    description: 'About the application'
  },
  {
    path: '*',
    element: <NotFound />,
    isPublic: true,
    name: 'Not Found',
    description: '404 error page'
  }
];

// Utility functions for route management
export const getRouteByPath = (path: string): RouteConfig | undefined => {
  return routes.find(route => route.path === path);
};

// Removed getProtectedRoutes: all routes now protected by profiles.subscription_status only.

export const getAdminRoutes = (): RouteConfig[] => {
  return routes.filter(route => route.requireAdmin);
};

export const getSubscriptionRoutes = (): RouteConfig[] => {
  return routes.filter(route => route.requireSubscription);
}; 